"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import { useCallback, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import BottomNavigation from "../navigation/BottomNavigation";
import { useViewContext } from "../navigation/ViewContext";
import BrainMobileWaves from "../brain/mobile/BrainMobileWaves";
import { useLayout } from "../brain/my-stream/layout/LayoutContext";
import HeaderPlaceholder from "../header/HeaderPlaceholder";
import { useHeaderContext } from "@/contexts/HeaderContext";
import { useDeepLinkNavigation } from "@/hooks/useDeepLinkNavigation";
import BrainMobileMessages from "../brain/mobile/BrainMobileMessages";
import { useSelector } from "react-redux";
import { selectEditingDropId } from "@/store/editSlice";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useAndroidKeyboard } from "@/hooks/useAndroidKeyboard";
import useCapacitor from "@/hooks/useCapacitor";
import PullToRefresh from "../providers/PullToRefresh";
const TouchDeviceHeader = dynamic(() => import("../header/AppHeader"), {
  ssr: false,
  loading: () => <HeaderPlaceholder />
});
interface Props {
  readonly children: ReactNode;
}
export default function AppLayout(t0) {
  const $ = _c(29);
  const {
    children
  } = t0;
  useDeepLinkNavigation();
  const {
    registerRef
  } = useLayout();
  const {
    setHeaderRef
  } = useHeaderContext();
  const headerRef = useRef(null);
  const {
    activeView
  } = useViewContext();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams?.get("drop");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isSingleDropOpen = t1 !== null;
  let t2;
  if ($[2] !== searchParams) {
    t2 = searchParams?.get("wave");
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const waveParam = t2;
  let t3;
  if ($[4] !== searchParams) {
    t3 = searchParams?.get("view");
    $[4] = searchParams;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const viewParam = t3;
  const hasWaveParam = Boolean(waveParam);
  const isViewingWavesOrMessages = viewParam === "waves" || viewParam === "messages";
  const isStreamRoute = pathname === "/waves" || pathname === "/messages" || pathname === "/notifications" || pathname === "/" && (hasWaveParam || isViewingWavesOrMessages);
  const editingDropId = useSelector(selectEditingDropId);
  const {
    isApp
  } = useDeviceInfo();
  const {
    isVisible: isAndroidKeyboardVisible,
    isAndroid
  } = useAndroidKeyboard();
  const {
    isIos,
    keyboardVisible: isIosKeyboardVisible
  } = useCapacitor();
  const isEditingOnMobile = isApp && editingDropId !== null;
  const isKeyboardVisible = isAndroid && isAndroidKeyboardVisible || isIos && isIosKeyboardVisible;
  const shouldHideBottomNav = isKeyboardVisible;
  let t4;
  if ($[6] !== registerRef || $[7] !== setHeaderRef) {
    t4 = node => {
      headerRef.current = node;
      registerRef("header", node);
      setHeaderRef(node);
    };
    $[6] = registerRef;
    $[7] = setHeaderRef;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const headerWrapperRef = t4;
  const isNavVisible = !isSingleDropOpen && !isEditingOnMobile && !shouldHideBottomNav;
  const safeAreaClass = !isNavVisible && !isKeyboardVisible ? "tw-pb-[env(safe-area-inset-bottom,0px)]" : "";
  const t5 = `${safeAreaClass} ${"tw-overflow-auto"}`;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <PullToRefresh triggerZoneRef={headerRef} />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <TouchDeviceHeader />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== headerWrapperRef) {
    t8 = <div ref={headerWrapperRef}>{t7}</div>;
    $[11] = headerWrapperRef;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== activeView || $[14] !== children) {
    t9 = activeView === "messages" ? <BrainMobileMessages /> : activeView === "waves" ? <BrainMobileWaves /> : <main>{children}</main>;
    $[13] = activeView;
    $[14] = children;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== isSingleDropOpen || $[17] !== isStreamRoute) {
    t10 = !isSingleDropOpen && !isStreamRoute && <div className="tw-h-16 tw-w-full" />;
    $[16] = isSingleDropOpen;
    $[17] = isStreamRoute;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== isEditingOnMobile || $[20] !== isSingleDropOpen || $[21] !== shouldHideBottomNav) {
    t11 = !isSingleDropOpen && !isEditingOnMobile && <BottomNavigation hidden={shouldHideBottomNav} />;
    $[19] = isEditingOnMobile;
    $[20] = isSingleDropOpen;
    $[21] = shouldHideBottomNav;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t5 || $[26] !== t8 || $[27] !== t9) {
    t12 = <div className={t5}>{t6}{t8}{t9}{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t5;
    $[26] = t8;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
}